import React, { useState, useEffect, useContext } from 'react';
import SEOHead from './SEOHead';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const initial = {
  title: '',
  description: '',
  event_type: 'free',
  fee: '',
  banner_url: '',
  event_date: '',
  duration: '',
  venue: '',
  online_link: '',
  speaker_name: '',
  category: '',
  capacity: '',
  agenda: '',
  status: 'active',
};

const PostEvent = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const isAdmin = user?.email === 'shifracs@gmail.com' || user?.email === 'Advisor@mednerd.in' || user?.role === 'admin' || user?.role === 'subadmin';
    if (!user || !isAdmin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    if (editId) {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/api/events/${editId}`, { headers: { Authorization: `Bearer ${token}` } });
          const ev = res.data.event;
          setForm({
            title: ev.title || '',
            description: ev.description || '',
            event_type: ev.event_type || 'free',
            fee: ev.fee || '',
            banner_url: ev.banner_url || '',
            event_date: ev.event_date ? ev.event_date.slice(0, 16) : '',
            duration: ev.duration || '',
            venue: ev.venue || '',
            online_link: ev.online_link || '',
            speaker_name: ev.speaker_name || '',
            category: ev.category || '',
            capacity: ev.capacity || '',
            agenda: ev.agenda || '',
            status: ev.status || 'active',
          });
        } catch (e) {
          toast.error('Failed to load event');
        }
      })();
    }
    // eslint-disable-next-line
  }, [editId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadBanner = async (file) => {
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Banner must be < 3MB');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/events/upload-banner`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      set('banner_url', res.data.banner_url);
      toast.success('Banner uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (form.event_type === 'paid' && (!form.fee || parseInt(form.fee, 10) <= 0)) {
      toast.error('Paid events must have a fee'); return;
    }

    const payload = {
      ...form,
      fee: form.event_type === 'paid' ? parseInt(form.fee, 10) : 0,
      capacity: form.capacity ? parseInt(form.capacity, 10) : null,
      event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
    };

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (editId) {
        await axios.put(`${API}/api/events/${editId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/api/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Event created');
      }
      navigate('/organizer-dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const base = parseInt(form.fee || 0, 10) || 0;
  const gst = Math.round(base * 0.18);
  const total = base + gst;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead title={editId ? 'Edit Event' : 'Post New Event'} description="Create or edit an event on Nerd Nation." path="/post-event" noIndex={true} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button onClick={() => navigate('/organizer-dashboard')} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">{editId ? 'Edit Event' : 'Post a New Event'}</h1>
        <p className="text-sm text-gray-600 mb-6">Create a workshop, webinar, conference or bootcamp</p>

        <Card><CardContent className="p-5 sm:p-6">
          <form onSubmit={submit} className="space-y-5">

            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" data-testid="event-title-input" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g., AI for Career Growth Workshop" required />
            </div>

            <div>
              <Label>Event Type *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => set('event_type', 'free')} className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${form.event_type === 'free' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`} data-testid="event-type-free">
                  🆓 Free Event
                </button>
                <button type="button" onClick={() => set('event_type', 'paid')} className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${form.event_type === 'paid' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`} data-testid="event-type-paid">
                  💳 Paid Event
                </button>
              </div>
            </div>

            {form.event_type === 'paid' && (
              <div>
                <Label htmlFor="fee">Base Fee (INR) *</Label>
                <Input id="fee" type="number" min="1" data-testid="event-fee-input" value={form.fee} onChange={(e) => set('fee', e.target.value)} placeholder="e.g., 500" required />
                {base > 0 && (
                  <div className="mt-2 text-xs text-gray-600 bg-blue-50 rounded-md px-3 py-2 border border-blue-100">
                    Final price for users: ₹{base.toLocaleString('en-IN')} + 18% GST (₹{gst.toLocaleString('en-IN')}) = <strong>₹{total.toLocaleString('en-IN')}</strong>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea id="description" data-testid="event-description-input" value={form.description} onChange={(e) => set('description', e.target.value)} rows={5} placeholder="What is this event about?" required />
            </div>

            <div>
              <Label>Banner Image</Label>
              {form.banner_url ? (
                <div className="relative">
                  <img src={form.banner_url} alt="" className="w-full aspect-video object-cover rounded-lg border border-gray-200" loading="lazy" decoding="async"/>
                  <button type="button" onClick={() => set('banner_url', '')} className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="h-6 w-6 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">{uploading ? 'Uploading…' : 'Click to upload banner (max 3MB)'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadBanner(e.target.files[0])} />
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event_date">Date & Time</Label>
                <Input id="event_date" type="datetime-local" value={form.event_date} onChange={(e) => set('event_date', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="duration">Duration</Label>
                <Input id="duration" value={form.duration} onChange={(e) => set('duration', e.target.value)} placeholder="e.g., 2 hours" />
              </div>
              <div>
                <Label htmlFor="venue">Venue</Label>
                <Input id="venue" value={form.venue} onChange={(e) => set('venue', e.target.value)} placeholder="Physical address or Online" />
              </div>
              <div>
                <Label htmlFor="online_link">Online Link</Label>
                <Input id="online_link" value={form.online_link} onChange={(e) => set('online_link', e.target.value)} placeholder="Zoom / Meet link (optional)" />
              </div>
              <div>
                <Label htmlFor="speaker_name">Speaker Name</Label>
                <Input id="speaker_name" value={form.speaker_name} onChange={(e) => set('speaker_name', e.target.value)} placeholder="Optional" />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <select id="category" value={form.category} onChange={(e) => set('category', e.target.value)} className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm">
                  <option value="">— Select —</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Webinar">Webinar</option>
                  <option value="Conference">Conference</option>
                  <option value="Bootcamp">Bootcamp</option>
                  <option value="Meetup">Meetup</option>
                  <option value="Hackathon">Hackathon</option>
                </select>
              </div>
              <div>
                <Label htmlFor="capacity">Capacity</Label>
                <Input id="capacity" type="number" min="1" value={form.capacity} onChange={(e) => set('capacity', e.target.value)} placeholder="Max attendees (optional)" />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <select id="status" value={form.status} onChange={(e) => set('status', e.target.value)} className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm">
                  <option value="active">Active (Published)</option>
                  <option value="draft">Draft</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="agenda">Agenda / Schedule</Label>
              <Textarea id="agenda" value={form.agenda} onChange={(e) => set('agenda', e.target.value)} rows={4} placeholder="Optional — outline of the event" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate('/organizer-dashboard')} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white" data-testid="event-submit-btn">
                {submitting ? 'Saving…' : (editId ? 'Update Event' : 'Publish Event')}
              </Button>
            </div>
          </form>
        </CardContent></Card>
      </div>
      <Footer />
    </div>
  );
};

export default PostEvent;
